import axios from "axios";
import { useMutation, useQuery } from "react-query";

/**
 * THE annotation-summary hook — POST /api/annotationExplorer/getAnnotationsSummary
 * → the shared annotationSummarizer. Used by the Annotation Explorer and the Lenses
 * v2 feature widget (there is one endpoint + one hook).
 *
 * Expensive on a cache miss (an LLM call), so it's gated by `enabled` — the caller
 * decides WHEN it fires (the Explorer waits for an explicit trigger; the feature
 * widget waits for a resolved findQuery). Keyed by scope + config so each distinct
 * summary caches separately in react-query (the BE caches too).
 *
 * @param {object}   args
 * @param {object}   [args.scope]        { activeFilters?, contentTypes?, findQuery? }
 * @param {string}   [args.strategy]     "dynamic" | "singleShot" | "aiChat"
 * @param {object}   [args.outputLength] { words?, maxSentences? }
 * @param {string}   [args.outputFormat] "markdown" | "plainText"
 * @param {string}   [args.mainInstructionsKey]
 * @param {boolean}  [args.enabled]
 * @param {object}   [args.queryOptions] extra react-query options (onError, staleTime, …)
 */
export const useGetAnnotationsSummary = ({
  scope = {},
  strategy,
  outputLength,
  outputFormat,
  mainInstructionsKey,
  // Probe: BE answers from its cache or not at all — never an LLM run.
  cacheOnly,
  enabled = true,
  queryOptions = {},
} = {}) => {
  const body = { scope, strategy, outputLength, outputFormat, mainInstructionsKey, cacheOnly };
  return useQuery({
    queryKey: ["annotationSummary", body],
    queryFn: () => axios.post("/api/annotationExplorer/getAnnotationsSummary", body).then((r) => r.data),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
};

/**
 * Dropdown options ({ strategies, scopes }) for the AI Engine Manager
 * "Summary Playground" tab. The menu lives in okf-be
 * ai/annotationSummarizer/CONFIG.js (PLAYGROUND_CONFIG) — new strategies added
 * there show up in the playground with zero FE changes.
 */
export const useGetAnnotationSummaryPlaygroundConfig = () =>
  useQuery({
    queryKey: ["annotationSummaryPlaygroundConfig"],
    queryFn: () => axios.get("/api/playground/annotationSummaryConfig").then((r) => r.data),
  });

/**
 * Playground runs — same endpoint as useGetAnnotationsSummary, but as a
 * MUTATION: every run must hit the BE fresh (forceRefresh: true) and repeat
 * runs of the same config must not be served from the react-query cache, since
 * the whole point is comparing regenerated outputs.
 */
export const useGenerateAnnotationSummaryPlayground = () =>
  useMutation(({ scope = {}, strategy }) =>
    axios
      .post("/api/annotationExplorer/getAnnotationsSummary", { scope, strategy, forceRefresh: true })
      .then((r) => r.data)
  );
